import { supabase } from '@/integrations/supabase/client';

/**
 * Extracts the filename from a Supabase storage URL
 * Example: https://mtzgsiyrjoqihggjrmqv.supabase.co/storage/v1/object/public/business-assets/filename.png
 * Returns: filename.png
 */
export function extractFilenameFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;

  try {
    // Handle both full URLs and already-extracted filenames
    if (!url.includes('/')) {
      return url;
    }

    // Extract filename from URL
    const urlParts = url.split('/');
    const filename = urlParts[urlParts.length - 1];

    // Remove any query parameters
    return filename.split('?')[0];
  } catch (error) {
    console.error('Error extracting filename from URL:', url, error);
    return null;
  }
}

/**
 * Deletes a single image from Supabase storage
 */
export async function deleteImageFromStorage(imageUrl: string | null | undefined): Promise<boolean> {
  if (!imageUrl) return false;

  const filename = extractFilenameFromUrl(imageUrl);
  if (!filename) {
    console.warn('Could not extract filename from URL:', imageUrl);
    return false;
  }

  try {
    const { error } = await supabase
      .storage
      .from('business-assets')
      .remove([filename]);

    if (error) {
      console.error('Error deleting image from storage:', filename, error);
      return false;
    }

    console.log('Successfully deleted image:', filename);
    return true;
  } catch (error) {
    console.error('Error deleting image from storage:', filename, error);
    return false;
  }
}

/**
 * Deletes multiple images from a resolution object (1x, 2x, 3x)
 */
export async function deleteResolutionImages(resolutionUrls: { '1x': string; '2x': string; '3x': string } | null | undefined): Promise<void> {
  if (!resolutionUrls) return;

  const urls = [resolutionUrls['1x'], resolutionUrls['2x'], resolutionUrls['3x']];

  await Promise.all(
    urls.map(url => deleteImageFromStorage(url))
  );
}

/**
 * Checks if a URL is from Supabase storage (not an external API)
 */
function isSupabaseStorageUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return url.includes('supabase.co/storage/');
}

/**
 * Collects all image URLs from a loyalty program
 * Only includes URLs from Supabase storage (excludes external URLs like QR codes)
 */
export function collectProgramImageUrls(program: any): string[] {
  const urls: string[] = [];

  // Single URL fields (only if from Supabase storage)
  if (program.logo_url && isSupabaseStorageUrl(program.logo_url)) {
    urls.push(program.logo_url);
  }
  if (program.hero_image_url && isSupabaseStorageUrl(program.hero_image_url)) {
    urls.push(program.hero_image_url);
  }
  if (program.google_logo_url && isSupabaseStorageUrl(program.google_logo_url)) {
    urls.push(program.google_logo_url);
  }
  // Skip qr_code_url - it's from api.qrserver.com, not our storage

  // Multi-resolution fields
  if (program.apple_logo_urls) {
    if (program.apple_logo_urls['1x']) urls.push(program.apple_logo_urls['1x']);
    if (program.apple_logo_urls['2x']) urls.push(program.apple_logo_urls['2x']);
    if (program.apple_logo_urls['3x']) urls.push(program.apple_logo_urls['3x']);
  }

  if (program.hero_image_urls) {
    if (program.hero_image_urls['1x']) urls.push(program.hero_image_urls['1x']);
    if (program.hero_image_urls['2x']) urls.push(program.hero_image_urls['2x']);
    if (program.hero_image_urls['3x']) urls.push(program.hero_image_urls['3x']);
  }

  return urls;
}

/**
 * Deletes all images associated with a loyalty program
 */
export async function deleteAllProgramImages(program: any): Promise<void> {
  const urls = collectProgramImageUrls(program);

  console.log(`Deleting ${urls.length} images for loyalty program:`, program.id);

  // Delete all images in parallel
  const results = await Promise.allSettled(
    urls.map(url => deleteImageFromStorage(url))
  );

  // Log results
  const successful = results.filter(r => r.status === 'fulfilled' && r.value === true).length;
  const failed = results.length - successful;

  console.log(`Image deletion complete: ${successful} successful, ${failed} failed`);
}

/**
 * Compares old and new image URLs and deletes only the old ones that changed
 * Used during program updates to clean up replaced images
 */
export async function deleteReplacedImages(
  oldProgram: any,
  newImages: {
    appleLogoUrls?: { '1x': string; '2x': string; '3x': string } | null;
    googleLogoUrl?: string | null;
    heroImageUrls?: { '1x': string; '2x': string; '3x': string } | null;
  }
): Promise<void> {
  const deletionPromises: Promise<boolean>[] = [];

  // Check Apple logo
  if (newImages.appleLogoUrls && oldProgram.apple_logo_urls) {
    // If URLs are different, delete old ones
    if (JSON.stringify(oldProgram.apple_logo_urls) !== JSON.stringify(newImages.appleLogoUrls)) {
      console.log('Apple logo changed, deleting old images');
      deletionPromises.push(
        deleteImageFromStorage(oldProgram.apple_logo_urls['1x']),
        deleteImageFromStorage(oldProgram.apple_logo_urls['2x']),
        deleteImageFromStorage(oldProgram.apple_logo_urls['3x'])
      );
    }
  }

  // Check Google logo
  if (newImages.googleLogoUrl && oldProgram.google_logo_url) {
    if (oldProgram.google_logo_url !== newImages.googleLogoUrl) {
      console.log('Google logo changed, deleting old image');
      deletionPromises.push(deleteImageFromStorage(oldProgram.google_logo_url));
    }
  }

  // Check hero image
  if (newImages.heroImageUrls && oldProgram.hero_image_urls) {
    if (JSON.stringify(oldProgram.hero_image_urls) !== JSON.stringify(newImages.heroImageUrls)) {
      console.log('Hero image changed, deleting old images');
      deletionPromises.push(
        deleteImageFromStorage(oldProgram.hero_image_urls['1x']),
        deleteImageFromStorage(oldProgram.hero_image_urls['2x']),
        deleteImageFromStorage(oldProgram.hero_image_urls['3x'])
      );
    }
  }

  // Execute all deletions in parallel
  if (deletionPromises.length > 0) {
    const results = await Promise.allSettled(deletionPromises);
    const successful = results.filter(r => r.status === 'fulfilled' && r.value === true).length;
    console.log(`Deleted ${successful} old images during update`);
  }
}
